import axios, { AxiosPromise } from "axios";
import { dashboardApiConfig, ops } from "./config";
import {
  BulkItemIdsRequestDto,
  DataKitAssignmentDto,
  DataKitDeleteResultDto,
  KitDeletePreviewDto,
  DataKitDiscardResultDto,
  DataKitDto,
  DataKitGroupDto,
  DataKitImportPreviewDto,
  DataKitImportRequestDto,
  DataKitImportResultResponseDto,
  DataKitItemDto,
  DataKitPublishDiffDto,
  DataKitPublishEventDto,
  DataKitPublishResultDto,
  ReparentGroupRequestDto,
  DataKitTagDto,
  GroupNoteRequestDto,
  ImportParseDto,
  MembershipRequestDto,
  MenuPriceRequestDto,
  GroupMenusDto,
  ItemMenuDto,
  MenuDetailDto,
  MenuSummaryDto,
  ReviewChangesDto,
  MergeTagsRequestDto,
  RenameGroupRequestDto,
  RenameTagRequestDto,
  SetAvailabilityRequestDto,
  SubgroupRequestDto,
  UnassignPreviewDto,
  VenueItemDto,
  VenuePriceOverrideRequestDto,
} from "../domain/DataKitDto";

const base = () => `${dashboardApiConfig.basePath}/data-kits`;
const kit = (kitId: string) => `${base()}/${encodeURIComponent(kitId)}`;
const item = (itemId: string) => `${base()}/items/${encodeURIComponent(itemId)}`;

const DataKitApi = {
  listKits(includeArchived = false): AxiosPromise<DataKitDto[]> {
    return axios.get(`${base()}?includeArchived=${includeArchived}`, ops());
  },
  getKit(kitId: string): AxiosPromise<DataKitDto> {
    return axios.get(kit(kitId), ops());
  },
  createKit(dto: DataKitDto): AxiosPromise<DataKitDto> {
    return axios.post(base(), dto, ops());
  },
  updateKit(kitId: string, dto: DataKitDto): AxiosPromise<DataKitDto> {
    return axios.put(kit(kitId), dto, ops());
  },
  archiveKit(kitId: string): AxiosPromise<DataKitDto> {
    return axios.put(`${kit(kitId)}/archive`, {}, ops());
  },
  unarchiveKit(kitId: string): AxiosPromise<DataKitDto> {
    return axios.put(`${kit(kitId)}/unarchive`, {}, ops());
  },
  previewDeleteKit(kitId: string): AxiosPromise<KitDeletePreviewDto> {
    return axios.get(`${kit(kitId)}/delete-preview`, ops());
  },
  deleteKit(kitId: string): AxiosPromise<DataKitDeleteResultDto> {
    return axios.delete(kit(kitId), ops());
  },

  listItems(kitId: string, includeArchived = false): AxiosPromise<DataKitItemDto[]> {
    return axios.get(`${kit(kitId)}/items?includeArchived=${includeArchived}`, ops());
  },
  getItem(itemId: string): AxiosPromise<DataKitItemDto> {
    return axios.get(item(itemId), ops());
  },
  createItem(kitId: string, dto: DataKitItemDto): AxiosPromise<DataKitItemDto> {
    return axios.post(`${kit(kitId)}/items`, dto, ops());
  },
  updateItem(itemId: string, dto: DataKitItemDto): AxiosPromise<DataKitItemDto> {
    return axios.put(item(itemId), dto, ops());
  },
  archiveItem(itemId: string): AxiosPromise<DataKitItemDto> {
    return axios.put(`${item(itemId)}/archive`, {}, ops());
  },
  unarchiveItem(itemId: string): AxiosPromise<DataKitItemDto> {
    return axios.put(`${item(itemId)}/unarchive`, {}, ops());
  },
  deleteItem(itemId: string): AxiosPromise<void> {
    return axios.delete(item(itemId), ops());
  },

  addMembership(itemId: string, dto: MembershipRequestDto): AxiosPromise<DataKitItemDto> {
    return axios.post(`${item(itemId)}/groups`, dto, ops());
  },
  removeMembership(itemId: string, groupName: string): AxiosPromise<DataKitItemDto> {
    return axios.delete(`${item(itemId)}/groups?groupName=${encodeURIComponent(groupName)}`, ops());
  },

  reparentGroup(kitId: string, dto: ReparentGroupRequestDto): AxiosPromise<DataKitGroupDto> {
    return axios.put(`${kit(kitId)}/groups/reparent`, dto, ops());
  },

  setAvailability(kitId: string, dto: SetAvailabilityRequestDto): AxiosPromise<DataKitItemDto[]> {
    return axios.put(`${kit(kitId)}/items/bulk/availability`, dto, ops());
  },
  bulkDeleteItems(kitId: string, dto: BulkItemIdsRequestDto): AxiosPromise<{ deleted: number }> {
    return axios.put(`${kit(kitId)}/items/bulk/delete`, dto, ops());
  },
  /** Distinct designs using any of these items - the bulk delete confirm's count. */
  previewBulkDeleteItems(kitId: string, dto: BulkItemIdsRequestDto): AxiosPromise<{ designCount: number }> {
    return axios.post(`${kit(kitId)}/items/bulk/delete-preview`, dto, ops());
  },
  bulkArchiveItems(kitId: string, dto: BulkItemIdsRequestDto): AxiosPromise<DataKitItemDto[]> {
    return axios.put(`${kit(kitId)}/items/bulk/archive`, dto, ops());
  },
  bulkRestoreItems(kitId: string, dto: BulkItemIdsRequestDto): AxiosPromise<DataKitItemDto[]> {
    return axios.put(`${kit(kitId)}/items/bulk/unarchive`, dto, ops());
  },

  /** Item Libraries › Menus: every menu (design using library items) on the account. */
  listAccountMenus(): AxiosPromise<MenuSummaryDto[]> {
    return axios.get(`${base()}/menus/account`, ops());
  },
  /** A menu's read-only view - its libraries and menu prices. */
  getMenu(designId: string): AxiosPromise<MenuDetailDto> {
    return axios.get(`${base()}/menus/account/designs/${encodeURIComponent(designId)}`, ops());
  },
  /** A library's Menus tab - the menus using its items. */
  listKitMenus(kitId: string): AxiosPromise<MenuSummaryDto[]> {
    return axios.get(`${kit(kitId)}/menus/designs`, ops());
  },
  /** Each group's menus, for its "Used on" line. */
  listGroupMenus(kitId: string): AxiosPromise<GroupMenusDto[]> {
    return axios.get(`${kit(kitId)}/groups/menu-designs`, ops());
  },
  /** "Used on N menus", opened - each menu showing the item, with its price there. */
  listItemMenus(itemId: string): AxiosPromise<ItemMenuDto[]> {
    return axios.get(`${item(itemId)}/menus`, ops());
  },
  /** `menuId` is the menu's design id. */
  setMenuPrice(itemId: string, dto: MenuPriceRequestDto): AxiosPromise<DataKitItemDto> {
    return axios.put(`${base()}/menus/items/${encodeURIComponent(itemId)}/price`, dto, ops());
  },
  listGroups(kitId: string): AxiosPromise<DataKitGroupDto[]> {
    return axios.get(`${kit(kitId)}/groups`, ops());
  },
  createGroup(kitId: string, dto: SubgroupRequestDto): AxiosPromise<DataKitGroupDto> {
    return axios.post(`${kit(kitId)}/groups`, dto, ops());
  },
  renameGroup(kitId: string, dto: RenameGroupRequestDto): AxiosPromise<DataKitGroupDto> {
    return axios.put(`${kit(kitId)}/groups/rename`, dto, ops());
  },
  setGroupNote(kitId: string, dto: GroupNoteRequestDto): AxiosPromise<DataKitGroupDto> {
    return axios.put(`${kit(kitId)}/groups/note`, dto, ops());
  },
  deleteGroup(kitId: string, name: string, path: string[] = []): AxiosPromise<void> {
    // `path` is repeatable, root first, and identifies a nested group unambiguously; `name` stays
    // as the single-level fallback the server uses when no path is sent.
    const parts = path.map((p) => `path=${encodeURIComponent(p)}`).join("&");
    const query = `name=${encodeURIComponent(name)}${parts ? `&${parts}` : ""}`;
    return axios.delete(`${kit(kitId)}/groups?${query}`, ops());
  },

  listTags(kitId: string): AxiosPromise<DataKitTagDto[]> {
    return axios.get(`${kit(kitId)}/tags`, ops());
  },
  autocompleteTags(kitId: string): AxiosPromise<string[]> {
    return axios.get(`${kit(kitId)}/tags/autocomplete`, ops());
  },
  renameTag(kitId: string, dto: RenameTagRequestDto): AxiosPromise<DataKitTagDto> {
    return axios.put(`${kit(kitId)}/tags/rename`, dto, ops());
  },
  mergeTags(kitId: string, dto: MergeTagsRequestDto): AxiosPromise<DataKitTagDto> {
    return axios.put(`${kit(kitId)}/tags/merge`, dto, ops());
  },

  getPublishDiff(kitId: string): AxiosPromise<DataKitPublishDiffDto> {
    return axios.get(`${kit(kitId)}/publish/diff`, ops());
  },
  publishKit(kitId: string): AxiosPromise<DataKitPublishResultDto> {
    return axios.post(`${kit(kitId)}/publish`, {}, ops());
  },
  discardKit(kitId: string): AxiosPromise<DataKitDiscardResultDto> {
    return axios.post(`${kit(kitId)}/discard`, {}, ops());
  },
  /** Review changes: everything waiting to publish, field by field, grouped by item. */
  reviewChanges(kitId: string): AxiosPromise<ReviewChangesDto> {
    return axios.get(`${kit(kitId)}/changes`, ops());
  },
  /** One item back to its published values - returns the list as it stands after. */
  discardItemChanges(kitId: string, itemId: string): AxiosPromise<ReviewChangesDto> {
    return axios.post(`${kit(kitId)}/changes/items/${encodeURIComponent(itemId)}/discard`, {}, ops());
  },
  /** A group's own waiting name/note/position change back to published. */
  discardGroupChanges(kitId: string, groupId: string): AxiosPromise<ReviewChangesDto> {
    return axios.post(`${kit(kitId)}/changes/groups/${encodeURIComponent(groupId)}/discard`, {}, ops());
  },
  getDesignPublishEvents(designId: string): AxiosPromise<DataKitPublishEventDto[]> {
    return axios.get(`${base()}/designs/${encodeURIComponent(designId)}/publish-events`, ops());
  },

  listAssignments(kitId: string): AxiosPromise<DataKitAssignmentDto[]> {
    return axios.get(`${kit(kitId)}/assignments`, ops());
  },
  createAssignment(kitId: string, dto: DataKitAssignmentDto): AxiosPromise<DataKitAssignmentDto[]> {
    return axios.post(`${kit(kitId)}/assignments`, dto, ops());
  },
  previewUnassign(kitId: string, unitType: string, unitId: string): AxiosPromise<UnassignPreviewDto> {
    return axios.get(
      `${kit(kitId)}/assignments/preview-unassign?unitType=${encodeURIComponent(unitType)}&unitId=${encodeURIComponent(unitId)}`,
      ops()
    );
  },
  deleteAssignment(kitId: string, unitType: string, unitId: string): AxiosPromise<DataKitAssignmentDto[]> {
    return axios.delete(
      `${kit(kitId)}/assignments?unitType=${encodeURIComponent(unitType)}&unitId=${encodeURIComponent(unitId)}`,
      ops()
    );
  },

  setVenuePriceOverride(
    unitType: string,
    unitId: string,
    externalId: string,
    dto: VenuePriceOverrideRequestDto
  ): AxiosPromise<VenueItemDto> {
    return axios.put(
      `${base()}/venues/${encodeURIComponent(unitType)}/${encodeURIComponent(unitId)}/items/${encodeURIComponent(externalId)}/price`,
      dto,
      ops()
    );
  },
  listVenueItems(unitType: string, unitId: string): AxiosPromise<VenueItemDto[]> {
    return axios.get(
      `${base()}/venues/${encodeURIComponent(unitType)}/${encodeURIComponent(unitId)}/items`,
      ops()
    );
  },

  parseImport(kitId: string, file: File): AxiosPromise<ImportParseDto> {
    const form = new FormData();
    form.append("file", file);
    return axios.post(`${kit(kitId)}/import/parse`, form, ops());
  },
  previewImport(kitId: string, dto: DataKitImportRequestDto): AxiosPromise<DataKitImportPreviewDto> {
    return axios.post(`${kit(kitId)}/import/preview`, dto, ops());
  },
  applyImport(kitId: string, dto: DataKitImportRequestDto): AxiosPromise<DataKitImportResultResponseDto> {
    return axios.post(`${kit(kitId)}/import`, dto, ops());
  },
};

export default DataKitApi;
